'use client';

import { useState } from "react";
import Container from "@/components/Container";
import Button from "@/components/Button";
import { apiPost } from "@/lib/api";
import Logo from "@/components/Logo";

type TokenOut = { access_token: string; role: "admin" | "manager" | "guest" };

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onLogin() {
    setErr(null);
    setLoading(true);
    try {
      const tok = await apiPost<TokenOut>("/auth/login", { email, password });
      localStorage.setItem("token", tok.access_token);
      localStorage.setItem("role", tok.role);
      if (tok.role === "admin") window.location.href = "/portal/admin";
      else if (tok.role === "guest") window.location.href = "/portal/guest";
      else window.location.href = "/portal/manager";
    } catch {
      setErr("Login failed. Check your email and password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-center">
      <div className="space-y-6">
        <Logo />
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-800">Rehab guest access</p>
          <h1 className="mt-3 text-4xl font-serif text-[#1a1c18]">Welcome back.</h1>
          <p className="mt-3 text-base text-[#3c4038]">
            Log in to view your stay details, upcoming check-ins, and booking history.
          </p>
        </div>
        <div className="rounded-2xl bg-[#1a2a22] p-6 text-sm text-[#f7f2ea] shadow-[0_24px_60px_-45px_rgba(16,24,40,0.8)]">
          <p className="font-semibold">Need access?</p>
          <p className="mt-2 text-[#d7e4db]">
            Use the set-password link from your booking confirmation email to activate your account.
          </p>
        </div>
      </div>

      <div className="rounded-[28px] bg-white/90 p-8 shadow-[0_40px_90px_-60px_rgba(21,34,26,0.95)] ring-1 ring-black/5 backdrop-blur">
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-[#1a1c18]">Guest login</h2>
          <p className="text-sm text-[#5b5f57]">Access your bookings and stay details.</p>
        </div>
        <div className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-[#2f322c]">Email</label>
            <input
              className="mt-2 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#1a1c18] shadow-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#2f322c]">Password</label>
            <input
              type={showPassword ? "text" : "password"}
              className="mt-2 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#1a1c18] shadow-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="mt-2 text-xs font-semibold text-emerald-700 hover:underline"
            >
              {showPassword ? "Hide password" : "Show password"}
            </button>
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
          <Button onClick={onLogin} disabled={loading} className="w-full">
            {loading ? "Signing in..." : "Sign in"}
          </Button>
          <a className="text-xs font-semibold text-emerald-700 hover:underline" href="/portal/forgot-password">
            Forgot password?
          </a>
          <p className="text-xs text-[#777b73]">
            Need a password? Use the set-password link in your booking email.
          </p>
        </div>
        <div className="mt-6 text-sm">
          <a className="text-emerald-700 hover:underline" href="/">{"<- Back to home"}</a>
        </div>
      </div>
    </Container>
  );
}
